// What the client opens.
//
// A document, not an app (client-surface.md §3): centered, calm, no rail, and no
// count of what they cannot see. It should read like something they were sent that
// happens to be current.
//
// **Four tabs, three levels.** It used to be one scroll that rendered every board,
// every group and every item — so a busy engagement arrived as sixty rows of equal
// weight and nothing told a client what to look at. The shape now is the one the
// internal client page took: a masthead and a fact strip that stay put, a nav row
// under them, and the rows one level down inside the project that owns them. A tab
// names exactly one record type, which is the rule the internal page follows and
// the reason this says Artifacts rather than "Delivered".
//
// **The board is the board.** The project screen renders the team's own sheet —
// `BoardSheet`, `BoardHeaders`, `BoardGroupBand`, `BoardRow`, the tracks in
// global.css — with Owner and the actions column dropped. It is not a second table
// that happens to look similar, because that is the arrangement where the client's
// page quietly drifts until it shows something the team's board does not.
//
// It does not use the store. The store syncs the whole log, which is precisely
// what must never reach this page — the payload arrives already filtered from
// `/api/client/:token` (see worker/client-view.ts) and this file only draws it.
// Keeping it off the store is what stops a future refactor quietly re-introducing
// the leak.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import {
  applyOnly,
  BlockedChip,
  BoardGrid,
  BoardGroupBand,
  BoardHeaders,
  BoardRow,
  BoardSheet,
  OnlyFilter,
  type BoardColumn,
  type Only,
  type Sort,
} from '../components/BoardSheet'
import { Avatar } from '../components/common'
import {
  doorButton,
  DoorFinePrint,
  DoorHeading,
  DoorLabel,
  doorField,
  Doorway,
} from '../components/Doorway'
import { Composer } from '../components/EntryThread'
import { Icon } from '../components/Icon'
import { Brand as Mark } from '../components/Mark'
import { Markdown } from '../components/Markdown'
import { MentionProvider, useListScope, type MentionCandidate } from '../components/Mentions'
import { SubTabs } from '../components/Page'
import { Priority } from '../components/Priority'
import { RowCardMark, RowCardPanel } from '../components/RowCardView'
import { blobUrl, workspaceSlug } from '../lib/api'
import { DEFAULT_BRAND, displayName, normalizeBrand, type Brand } from '../lib/brand'
import { dueIn, formatDate, hostPath, relativeTime, todayIso } from '../lib/format'
import { dueTone, type RecordLink } from '../lib/model'
import { applyBrand } from '../lib/theme'

interface Item {
  id: string
  label: string
  status: string
  done: boolean
  due: string
  blocked: boolean
  waitingOn: string[]
  blocks: number
  priority: number
  mine: boolean
  note: string
  comments: Comment[]
}
interface Comment {
  id: string
  body: string
  by: string
  mine: boolean
  at: string
}
interface Group {
  id: string
  label: string
  due: string
  items: Item[]
}
interface Counts {
  open: number
  inFlight: number
  blocked: number
  done: number
}
interface Project {
  id: string
  name: string
  description: string
  kind: string
  state: 'running' | 'held' | 'finished'
  counts: Counts
  nextDue: string
  groups: Group[]
}
interface Artifact {
  id: string
  name: string
  kind: string
  end: string
  links: RecordLink[]
}
interface View {
  org: { id: string; name: string; description: string }
  engagement: string
  updatedAt: string
  counts: Counts
  projects: Project[]
  milestones: Array<{ id: string; label: string; due: string; board: string; project: string }>
  artifacts: Artifact[]
  people: Array<{ id: string; name: string; role: string }>
}

type Writer = (payload: Record<string, unknown>) => Promise<void>

type Tab = 'overview' | 'projects' | 'artifacts' | 'team'
const TABS: Tab[] = ['overview', 'projects', 'artifacts', 'team']

export function ClientSeat() {
  const { slug = '', token = '' } = useParams()
  const [view, setView] = useState<View | null>(null)
  const [me, setMe] = useState<{
    id: string
    name: string
    email: string
    staff: boolean
  } | null>(null)
  const [brand, setBrand] = useState<Brand | null>(null)
  const [error, setError] = useState('')
  // Separate from `error`: not being signed in is not a failure, it is the first
  // screen. The two used to be one string and the page read like something had
  // gone wrong when nothing had.
  const [needsSignIn, setNeedsSignIn] = useState(false)

  // Tab and project live in the query string, not in state: going back from a
  // board should return you to the list you opened it from, and a client who
  // bookmarks a project should land on it. The internal client page carries its
  // tab the same way, for the same reason.
  const [params, setParams] = useSearchParams()
  const rawTab = params.get('tab') as Tab | null
  const tab: Tab = rawTab && TABS.includes(rawTab) ? rawTab : 'overview'
  const openId = params.get('project') ?? ''

  const go = (next: Tab, project?: string) => {
    const p = new URLSearchParams(params)
    if (next === 'overview') p.delete('tab')
    else p.set('tab', next)
    if (project) p.set('project', project)
    else p.delete('project')
    setParams(p)
  }

  // Every write goes to the one endpoint that accepts them, then re-reads. A
  // re-read rather than a local patch: the server decides what a write did (it may
  // refuse, and it stamps the actor), so the page should show what the log says
  // rather than what it hoped.
  const [busy, setBusy] = useState(false)
  const [writeError, setWriteError] = useState('')

  const load = useCallback(async () => {
    setError('')
    try {
      const res = await fetch(
        `/api/w/${encodeURIComponent(slug)}/client/${encodeURIComponent(token)}`,
      )
      if (res.status === 401) {
        setNeedsSignIn(true)
        // The brand still loads: the sign-in screen is the team's, and an
        // unbranded box asking a client for their email is the shape of a phish.
        const b = normalizeBrand(
          (await (await fetch(`/api/w/${encodeURIComponent(slug)}/brand`)).json().catch(() => ({})))
            .brand ?? {},
        )
        setBrand(b)
        applyBrand(b)
        return
      }
      if (!res.ok) {
        setError('This link is not valid. Ask for a new one.')
        return
      }
      const body = (await res.json()) as {
        view: View
        brand: Brand
        me: { id: string; name: string; email: string; staff: boolean }
      }
      setView(body.view)
      setMe(body.me)
      setNeedsSignIn(false)
      const b = normalizeBrand(body.brand)
      setBrand(b)
      applyBrand(b)
    } catch {
      setError('Could not load this page.')
    }
  }, [slug, token])

  useEffect(() => {
    void load()
  }, [load])

  const write = useCallback(
    async (payload: Record<string, unknown>) => {
      setBusy(true)
      setWriteError('')
      try {
        const res = await fetch(
          `/api/w/${encodeURIComponent(slug)}/client/${encodeURIComponent(token)}/write`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(payload),
          },
        )
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          setWriteError(body.error ?? 'That did not save.')
          return
        }
        await load()
      } catch {
        setWriteError('That did not save.')
      } finally {
        setBusy(false)
      }
    },
    [slug, token, load],
  )

  /**
   * What `@` may reach from this page: the people, projects and artifacts already in
   * the payload, and nothing else.
   *
   * The team's picker is backed by the whole folded log, which is exactly why this
   * page cannot borrow it — it would offer a client every org, deal and person the
   * team has. Backed by the view, it offers what the page is already showing them,
   * so making it typeable discloses nothing new. Built before the early returns
   * below, because a hook cannot run conditionally.
   */
  const rows = useMemo<MentionCandidate[]>(() => {
    if (!view) return []
    return [
      ...view.people.map((p) => ({ id: p.id, title: p.name, kind: 'person' as const })),
      ...view.projects.map((p) => ({ id: p.id, title: p.name, kind: 'project' as const })),
      ...view.artifacts.map((a) => ({ id: a.id, title: a.name, kind: 'artifact' as const })),
    ].filter((r) => r.title.trim())
  }, [view])
  // No `open`: this page has no record pages, so a chip names a thing rather than
  // pretending to link somewhere.
  const scope = useListScope(rows)

  if (error) {
    return (
      <Shell brand={brand}>
        <p className="text-body py-10 text-center" style={{ color: 'var(--muted)' }}>
          {error}
        </p>
      </Shell>
    )
  }
  if (needsSignIn) {
    // NOT inside the Shell. `Doorway` is a full-bleed split that sizes itself to the
    // viewport, and wrapping it in the document's 1180px column with a masthead
    // above it and a footer below gave it a `min-h-dvh` panel inside a page that was
    // already a page — two logos, two names, and a door that could not reach either
    // edge. The door is the whole screen, the way the team's is.
    return <SignInBox token={token} expired={params.get('expired') === '1'} />
  }
  if (!view) {
    return (
      <Shell brand={brand}>
        <p className="text-body py-10 text-center" style={{ color: 'var(--faint)' }}>
          Loading…
        </p>
      </Shell>
    )
  }

  const open = view.projects.find((p) => p.id === openId)

  return (
    <MentionProvider scope={scope}>
      <Shell brand={brand} updatedAt={view.updatedAt} me={me} token={token}>
        <header className="mb-6">
          <h1 className="text-masthead font-semibold tracking-tight">{view.org.name}</h1>
          {/* The app's own prose measure (the 68ch `.prose-body` uses), not a
              narrower one of this page's own. At 58ch under a 30px masthead in a
              960px column it wrapped at about 400px, which reads as a layout
              accident rather than as a measure somebody chose. */}
          {view.org.description && (
            <p className="prose-measure text-lead mt-2" style={{ color: 'var(--muted)' }}>
              {view.org.description}
            </p>
          )}
        </header>

        <SubTabs
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'projects', label: 'Projects', count: view.projects.length },
            { key: 'artifacts', label: 'Artifacts', count: view.artifacts.length },
            { key: 'team', label: 'Team', count: view.people.length },
          ]}
          active={tab}
          onPick={(k) => go(k as Tab)}
        />

        {tab === 'overview' && <Overview view={view} onOpen={go} />}
        {tab === 'projects' &&
          (open ? (
            <ProjectView
              project={open}
              onBack={() => go('projects')}
              write={me?.staff ? undefined : write}
              busy={busy}
            />
          ) : (
            <Projects view={view} onOpen={go} />
          ))}

        {writeError && (
          <p className="meta mt-4" style={{ color: 'var(--crit)' }}>
            {writeError}
          </p>
        )}
        {tab === 'artifacts' && <Artifacts artifacts={view.artifacts} />}
        {tab === 'team' && <Team people={view.people} />}
      </Shell>
    </MentionProvider>
  )
}

// ── overview ─────────────────────────────────────────────────────────────────

/**
 * Where things stand, then when. This page answers "how is it going" and hands off
 * to the tab that answers "what exactly", which is the whole reason the rows moved
 * one level down.
 */
function Overview({ view, onOpen }: { view: View; onOpen: (t: Tab, project?: string) => void }) {
  const running = view.projects.filter((p) => p.state !== 'finished')
  return (
    <>
      <Block
        title="Active projects"
        right={
          <button type="button" onClick={() => onOpen('projects')} className="meta underline">
            all projects
          </button>
        }
      >
        {running.length === 0 ? (
          <p className="note py-2">No active projects.</p>
        ) : (
          // `auto-fit`, not a fixed 2-then-3: a client with two projects got two
          // cards and an empty third of the row, because the column count was chosen
          // by the viewport rather than by how many cards there are.
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(17rem, 100%), 1fr))' }}
          >
            {running.slice(0, 6).map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => onOpen('projects', p.id)} />
            ))}
          </div>
        )}
      </Block>

      <Block title="Timeline">
        <Timeline milestones={view.milestones} />
      </Block>

      {view.artifacts.length > 0 && (
        <Block
          title="Latest artifacts"
          right={
            <button type="button" onClick={() => onOpen('artifacts')} className="meta underline">
              all {view.artifacts.length}
            </button>
          }
        >
          {view.artifacts.slice(0, 3).map((a) => (
            <ArtifactRow key={a.id} artifact={a} />
          ))}
        </Block>
      )}
    </>
  )
}

/**
 * One project, as a card: the state of it, the same segmented bar its board header
 * carries, and the next date. A card is a LINK, never the board itself — putting
 * the rows here is what made the old page a scroll.
 */
function ProjectCard({ project, onOpen }: { project: Project; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded-float border p-4 text-left"
      style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
    >
      <span className="flex items-baseline gap-2">
        <span className="text-lead flex-1 font-semibold tracking-tight">{project.name}</span>
        <StateChip state={project.state} />
      </span>
      {project.description && (
        <p className="text-body mt-1.5 line-clamp-2" style={{ color: 'var(--muted)' }}>
          {project.description}
        </p>
      )}
      <span className="mt-3 block">
        <CountBar counts={project.counts} />
      </span>
      <span className="meta mt-1.5 block">
        <Legend counts={project.counts} />
      </span>
      <span
        className="mt-3 flex items-baseline gap-2 border-t pt-2.5"
        style={{ borderColor: 'var(--rule-soft)' }}
      >
        {project.nextDue ? (
          <>
            <span
              className="num text-meta shrink-0 font-semibold"
              style={{ color: dueTone(project.nextDue) }}
            >
              {formatDate(project.nextDue)}
            </span>
            <span className="meta flex-1">{dueIn(project.nextDue)}</span>
          </>
        ) : (
          <span className="meta flex-1">No date</span>
        )}
        <Icon name="ArrowRight" size={14} style={{ color: 'var(--faint)' }} />
      </span>
    </button>
  )
}

/**
 * Every promised date on one axis, coloured by the project it belongs to, with
 * today marked.
 *
 * A list of five dates says less than five dots with the gaps between them drawn:
 * "nothing for six weeks, then three things in one" is the shape a client most
 * wants and the one a list cannot show. Labels alternate above and below, so two
 * near dates do not collide.
 */
function Timeline({ milestones }: { milestones: View['milestones'] }) {
  const today = todayIso()
  const ahead = useMemo(
    () => milestones.filter((m) => m.due >= today).slice(0, 8),
    [milestones, today],
  )

  if (ahead.length === 0) {
    return <p className="meta py-2">No dates yet.</p>
  }

  const day = (iso: string) => new Date(`${iso}T00:00:00`).getTime()
  const from = day(today)
  // A month of headroom past the last date, so the final dot is never jammed
  // against the right edge and the axis reads as continuing.
  const last = day(ahead[ahead.length - 1]!.due)
  const span = Math.max(last - from, 86_400_000 * 30) * 1.12
  const at = (iso: string) => `${(((day(iso) - from) / span) * 92 + 4).toFixed(2)}%`

  return (
    <>
      {/* On a phone the axis has no room: 140px labels over a 330px card collide
          whatever you do with them, so the same dates render as a list. The axis is
          the point of this section at desk width and a liability below it. */}
      <div className="sm:hidden">
        {ahead.map((m) => (
          <div
            key={m.id}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b py-2.5 last:border-b-0"
            style={{ borderColor: 'var(--rule-soft)' }}
          >
            <span
              className="num text-meta shrink-0 font-semibold"
              style={{ color: dueTone(m.due), minWidth: '6.5rem' }}
            >
              {formatDate(m.due)}
            </span>
            <span className="meta shrink-0">{dueIn(m.due)}</span>
            <span className="text-body min-w-0 flex-1 basis-full">
              {m.label}
              <span className="meta block">{m.board}</span>
            </span>
          </div>
        ))}
      </div>

      <div
        className="rounded-float relative hidden border sm:block"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)', height: 186 }}
      >
        <div
          className="absolute inset-x-0"
          style={{ top: 92, height: 1, background: 'var(--border)' }}
        />
        <div
          className="absolute"
          style={{ left: '4%', top: 14, bottom: 22, width: 2, background: 'var(--accent)' }}
        />
        <span
          className="eyebrow absolute"
          style={{ left: '4%', top: 96, paddingLeft: 8, color: 'var(--accent)' }}
        >
          Today
        </span>

        {ahead.map((m, i) => {
          const above = i % 2 === 0
          return (
            <span key={m.id}>
              <span
                className="absolute rounded-full"
                style={{
                  left: at(m.due),
                  top: 88,
                  width: 9,
                  height: 9,
                  marginLeft: -4.5,
                  background: dueTone(m.due),
                }}
              />
              <span
                className="absolute"
                style={{
                  left: at(m.due),
                  top: above ? 78 : 92,
                  width: 1,
                  height: 14,
                  background: 'var(--rule-soft)',
                }}
              />
              <span
                className="absolute text-center"
                style={{ left: at(m.due), top: above ? 22 : 108, width: 140, marginLeft: -70 }}
              >
                <span
                  className="num text-label block font-semibold"
                  style={{ color: dueTone(m.due) }}
                >
                  {formatDate(m.due)}
                </span>
                <span className="text-meta block leading-tight font-medium">{m.label}</span>
                <span className="meta block">{m.board}</span>
              </span>
            </span>
          )
        })}
      </div>
    </>
  )
}

// ── projects ─────────────────────────────────────────────────────────────────

/** The list. Finished projects stay: delivered work is the relationship's history,
 *  and archiving it out of view deletes the case for carrying on. */
function Projects({ view, onOpen }: { view: View; onOpen: (t: Tab, project?: string) => void }) {
  const running = view.projects.filter((p) => p.state !== 'finished')
  const finished = view.projects.filter((p) => p.state === 'finished')

  return (
    <>
      <Block title="Active">
        {running.length === 0 ? (
          <p className="meta py-2">No active projects.</p>
        ) : (
          <div className="flex flex-col gap-3.5">
            {running.map((p) => (
              <ProjectRow key={p.id} project={p} onOpen={() => onOpen('projects', p.id)} />
            ))}
          </div>
        )}
      </Block>

      {finished.length > 0 && (
        <Block title="Finished">
          <div className="flex flex-col gap-3.5">
            {finished.map((p) => (
              <ProjectRow key={p.id} project={p} onOpen={() => onOpen('projects', p.id)} />
            ))}
          </div>
        </Block>
      )}
    </>
  )
}

function ProjectRow({ project, onOpen }: { project: Project; onOpen: () => void }) {
  const total = project.counts.done + project.counts.open
  const rows = project.groups.flatMap((g) => g.items)
  const inFlight = rows
    .filter((i) => !i.blocked && !i.done && i.status === 'In flight')
    .map((i) => i.label)
  const blocked = rows.filter((i) => i.blocked).map((i) => i.label)
  const dated = project.groups.filter((g) => g.due).map((g) => `${formatDate(g.due)} · ${g.label}`)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded-float w-full border p-4 text-left"
      style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
    >
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-lead font-semibold tracking-tight">{project.name}</span>
        <StateChip state={project.state} />
        <span className="flex-1" />
        {project.nextDue && (
          <>
            <span className="meta">next</span>
            <span
              className="num text-meta font-semibold"
              style={{ color: dueTone(project.nextDue) }}
            >
              {formatDate(project.nextDue)}
            </span>
          </>
        )}
        <Icon name="ArrowRight" size={14} style={{ color: 'var(--faint)' }} />
      </span>
      {project.description && (
        <p className="text-body mt-1.5 max-w-[78ch]" style={{ color: 'var(--muted)' }}>
          {project.description}
        </p>
      )}
      <span className="mt-3 block">
        <CountBar counts={project.counts} />
      </span>
      <span className="meta mt-1.5 block">
        <Legend counts={project.counts} /> · {total} {total === 1 ? 'row' : 'rows'}
      </span>

      {/* What the Overview's card deliberately leaves out. A card answers "how is
          it going"; this list is the tab you opened to find out what, exactly, and
          naming three or four rows here is what makes it worth opening before you
          open the board. */}
      <span
        className="mt-3.5 grid gap-5 border-t pt-3 sm:grid-cols-3"
        style={{ borderColor: 'var(--rule-soft)' }}
      >
        <Breakdown title="In flight" tone="var(--brass)" lines={inFlight} empty="·" />
        <Breakdown title="Blocked" tone="var(--crit)" lines={blocked} empty="·" />
        <Breakdown title="Dated" tone="var(--muted)" lines={dated} empty="·" />
      </span>
    </button>
  )
}

/** One column of a project row: three rows at most, then a count. A list that
 *  grows with the board stops being a summary. */
function Breakdown({
  title,
  tone,
  lines,
  empty,
}: {
  title: string
  tone: string
  lines: string[]
  empty: string
}) {
  return (
    <span className="block">
      <span className="eyebrow block" style={{ color: tone }}>
        {title}
      </span>
      <span className="text-body mt-1 block leading-[1.55]">
        {lines.length === 0 ? (
          <span style={{ color: 'var(--faint)' }}>{empty}</span>
        ) : (
          <>
            {lines.slice(0, 3).map((l) => (
              <span key={l} className="block truncate">
                {l}
              </span>
            ))}
            {lines.length > 3 && <span className="meta block">and {lines.length - 3} more</span>}
          </>
        )}
      </span>
    </span>
  )
}

/**
 * One project, and its board.
 *
 * The header is the client page's own chrome — the bar, the counts, the next date.
 * The sheet under it is the team's board, which is the point: there is one board
 * and this is a seat on it.
 */
function ProjectView({
  project,
  onBack,
  write,
  busy,
}: {
  project: Project
  onBack: () => void
  write?: Writer | undefined
  busy: boolean
}) {
  return (
    <>
      <button type="button" onClick={onBack} className="meta mb-2 flex items-center gap-1">
        <Icon name="ChevronRight" size={12} style={{ transform: 'rotate(180deg)' }} />
        all projects
      </button>

      <div
        className="rounded-float mb-5 border p-4"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-title font-semibold tracking-tight">{project.name}</h2>
          <StateChip state={project.state} />
          <span className="flex-1" />
          {project.nextDue && (
            <span className="flex items-baseline gap-2">
              <span className="eyebrow">Next</span>
              <span
                className="num text-lead font-semibold"
                style={{ color: dueTone(project.nextDue) }}
              >
                {formatDate(project.nextDue)}
              </span>
            </span>
          )}
        </div>
        {project.description && (
          <p className="text-body mt-2 max-w-[78ch]" style={{ color: 'var(--muted)' }}>
            {project.description}
          </p>
        )}
        <div className="mt-3">
          <CountBar counts={project.counts} />
        </div>
        <div className="meta mt-1.5">
          <Legend counts={project.counts} />
        </div>
      </div>

      <ClientBoard work={project.id} groups={project.groups} write={write} busy={busy} />
    </>
  )
}

// ── the board ────────────────────────────────────────────────────────────────

/** Five of the team's seven. Owner names our people to them, and every control
 *  in the actions column — the eye, the row card, archive — is something only the
 *  team does. */
const CLIENT_COLUMNS: BoardColumn[] = [
  { key: 'item', label: 'Item', sortable: true },
  { key: 'waits', label: 'Waits on', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'due', label: 'Due', sortable: true },
  { key: 'theirs', label: 'Priority', sortable: true },
  { key: 'notes', label: 'Notes', sortable: false, end: true },
]

/**
 * The team's board, from the client's side of the wire.
 *
 * Everything structural — the sheet, the sticky header, the group bands, the
 * subgrid rows, the Blocked chip, the filter — is the shared component. What
 * differs is the cells: these are text where the team's are controls, because
 * the seat is read-only. When it can write, Priority is the column that becomes
 * live; nothing else about this function changes.
 */
function ClientBoard({
  work,
  groups,
  write,
  busy,
}: {
  work: string
  groups: Group[]
  write?: Writer | undefined
  busy: boolean
}) {
  const [sort, setSort] = useState<Sort>({ key: 'board', dir: 'asc' })
  const [only, setOnly] = useState<Only>('all')

  /**
   * Sorting happens WITHIN a group, never across them — a group is a dated
   * commitment, so its position is a decision someone made, and letting a column
   * reorder the groups would quietly rewrite the promise the board is making.
   */
  const sorted = (items: Item[]): Item[] => {
    const list = applyOnly(items, only)
    if (sort.key === 'board') return list
    const dir = sort.dir === 'asc' ? 1 : -1
    const cmp: Record<string, (a: Item, b: Item) => number> = {
      item: (a, b) => a.label.localeCompare(b.label) * dir,
      status: (a, b) => a.status.localeCompare(b.status) * dir,
      // Undated last whichever way the column points: a blank is not "soonest",
      // and flipping the header should not parade every empty cell to the top.
      due: (a, b) => (a.due ? 0 : 1) - (b.due ? 0 : 1) || a.due.localeCompare(b.due) * dir,
      theirs: (a, b) => (b.priority - a.priority) * dir,
    }
    const by = cmp[sort.key]
    return by ? [...list].sort(by) : list
  }

  if (groups.length === 0) {
    return <p className="note py-3">Nothing on this board yet.</p>
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="meta">Show</span>
        <OnlyFilter value={only} onChange={setOnly} />
      </div>

      <BoardSheet variant="client">
        <BoardHeaders columns={CLIENT_COLUMNS} sort={sort} onSort={setSort} />
        <BoardGrid>
          {groups.map((g) => {
            const items = sorted(g.items)
            return (
              // `display: contents` so the band and its rows stay direct grid
              // items and keep their subgrid columns — the same trick the team's
              // drop zones use.
              <div key={g.id} style={{ display: 'contents' }}>
                <BoardGroupBand
                  name={<span className="text-lead font-semibold tracking-tight">{g.label}</span>}
                  // The team's band reads "6 items · 4 shown to client". The
                  // second half is exactly the number that must never appear here.
                  count={`${g.items.length} ${g.items.length === 1 ? 'item' : 'items'}`}
                  right={
                    g.due ? (
                      <>
                        <span
                          className="num text-meta font-semibold"
                          style={{ color: dueTone(g.due) }}
                        >
                          {formatDate(g.due)}
                        </span>
                        <span className="meta">{dueIn(g.due)}</span>
                      </>
                    ) : (
                      <span className="meta">no date</span>
                    )
                  }
                />
                {items.map((i) => (
                  <ItemRow key={i.id} item={i} write={write} busy={busy} />
                ))}
                {items.length === 0 && (
                  <p className="note" style={{ gridColumn: '1 / -1', padding: '8px 0' }}>
                    Nothing here under this filter.
                  </p>
                )}
                {write && <AddRow work={work} group={g.id} write={write} busy={busy} />}
              </div>
            )
          })}
        </BoardGrid>
      </BoardSheet>
    </>
  )
}

/**
 * One row: the same cells the team's row renders, minus Owner and the controls
 * only the team has. Two of them are live.
 */
function ItemRow({ item, write, busy }: { item: Item; write?: Writer | undefined; busy: boolean }) {
  const [note, setNote] = useState(item.note)
  const [editingNote, setEditingNote] = useState(false)
  // The panel is a SIBLING of the row in the grid, not a child of a cell: a cell
  // cannot be full width. So the row renders a fragment, and the open state sits
  // here, on the row that owns both halves.
  const [open, setOpen] = useState(false)
  useEffect(() => setNote(item.note), [item.note])

  return (
    <>
      <BoardRow>
        <span className="min-w-0">
          <span
            className="text-body"
            style={{
              textDecoration: item.done ? 'line-through' : undefined,
              color: item.done ? 'var(--faint)' : undefined,
            }}
          >
            {item.label}
          </span>
          {/* A row they added, marked on their own side too: the team's board flags
            it for triage, and this is the same fact read from the other end. */}
          {item.mine && (
            <span className="meta ml-1.5" style={{ color: 'var(--brass)' }}>
              yours
            </span>
          )}
          {!item.blocked && item.blocks > 0 && (
            <span className="meta block" style={{ marginTop: -1 }}>
              {item.blocks} {item.blocks === 1 ? 'item waits' : 'items wait'} on this
            </span>
          )}
        </span>

        {/* A blocked row names what it waits on — but only blockers the client can
          also see. Blocked by something internal still reads as blocked, which is
          true and useful, and names nothing. */}
        <span className="text-meta min-w-0">
          {item.waitingOn.length > 0 && (
            <span className="flex min-w-0 flex-col">
              {item.waitingOn.map((label) => (
                <span
                  key={label}
                  className="truncate"
                  style={{ color: 'var(--crit)' }}
                  title={label}
                >
                  {label}
                </span>
              ))}
            </span>
          )}
        </span>

        {item.blocked ? (
          <BlockedChip blockers={item.waitingOn.length || 1} />
        ) : (
          <span className="text-meta" style={{ color: item.done ? 'var(--faint)' : undefined }}>
            {item.status}
          </span>
        )}

        {/* A finished item is never overdue. `dueTone` only knows the date, so a done
          row was showing its date in alarm red. */}
        <span
          className="num text-meta"
          style={{ color: item.done ? 'var(--faint)' : dueTone(item.due) }}
        >
          {item.due ? formatDate(item.due) : '·'}
        </span>

        {/* Theirs, and now actually theirs: the same control the team sees, writing
          the same field. The team sorts by it and never edits it. */}
        <span title="Your priority — click to raise">
          <Priority
            value={item.priority}
            onSet={
              !write || busy
                ? undefined
                : (v) => void write({ op: 'item.priority', item: item.id, priority: v })
            }
          />
        </span>

        <span className="flex justify-end">
          <RowCardMark
            hasNote={!!note.trim()}
            count={item.comments.length}
            open={open}
            onToggle={() => setOpen((v) => !v)}
            // Every note on a row that travels, not only their own: a mark that
            // opens onto an explanation of what you may not read is worse than no
            // mark. See worker/client-view.ts.
            note={note}
            comments={item.comments}
          />
        </span>
      </BoardRow>
      {open && (
        <RowCardPanel
          comments={item.comments}
          posting={busy}
          onPost={
            write ? (body) => void write({ op: 'comment.create', item: item.id, body }) : undefined
          }
          composer={
            // The same editor the team uses, over this page's own scope: they can
            // name a person on their team or a project of theirs, and nothing else.
            write ? (
              <Composer
                placeholder="Add a comment…"
                submitLabel="Post"
                submitOnEnter
                minHeight={44}
                onSave={(body) => void write({ op: 'comment.create', item: item.id, body })}
              />
            ) : (
              // A member reading someone else's page. Said out loud rather than
              // left as an absence: a thread with no box under it reads as a thread
              // nobody can post to, which is the opposite of what is true here.
              <p className="note">
                Staff preview. The client posts here; reply from the board instead.
              </p>
            )
          }
          noteSlot={
            <>
              <p className="eyebrow mb-1">Note</p>
              {/* The note travels with the row, whoever wrote it — but only a row
                  they added is theirs to rewrite; `client-write.ts` refuses the
                  rest. So a team row renders as prose and their own as an editor. */}
              {!item.mine || !write ? (
                note.trim() ? (
                  <Markdown body={note} className="prose-body prose-tight" />
                ) : (
                  <p className="note">Nothing written on this row.</p>
                )
              ) : editingNote ? (
                <div className="space-y-2">
                  <textarea
                    autoFocus
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    className="prose-body rounded-control w-full resize-none border px-2 py-1.5 outline-none"
                    style={{
                      borderColor: 'var(--border)',
                      background: 'var(--bg)',
                      fontSize: 'var(--text-body)',
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingNote(false)
                        void write({ op: 'item.note', item: item.id, note })
                      }}
                      className="rounded-control text-body px-3 py-1.5 font-medium"
                      style={{ background: 'var(--marine)', color: 'var(--panel)' }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNote(item.note)
                        setEditingNote(false)
                      }}
                      className="text-body px-2"
                      style={{ color: 'var(--muted)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingNote(true)}
                  className="block w-full text-left"
                >
                  {/* Markdown, like the read-only branch above and like the team's
                      own note: the same prose should not render two ways depending
                      on who is looking at it. */}
                  {note.trim() ? (
                    <Markdown body={note} className="prose-body prose-tight" />
                  ) : (
                    <span className="text-body" style={{ color: 'var(--faint)' }}>
                      Add a note…
                    </span>
                  )}
                </button>
              )}
            </>
          }
        />
      )}
    </>
  )
}

/**
 * Adding a row, in the group you are looking at.
 *
 * It lands in the log immediately and carries `addedBy: 'client'` — no approval
 * step. A row you typed that does not appear is a page that feels broken, and an
 * approval on every row is a queue that gets rubber-stamped. The team's board
 * marks it instead, so somebody re-files it rather than never seeing it.
 */
function AddRow({
  work,
  group,
  write,
  busy,
}: {
  work: string
  group: string
  write?: Writer | undefined
  busy: boolean
}) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const label = draft.trim()
    if (!label || busy || !write) return
    setDraft('')
    void write({ op: 'item.create', work, group, label })
  }
  return (
    <div style={{ gridColumn: '1 / -1' }} className="py-1.5">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && add()}
        placeholder="Add something to this group…"
        aria-label="Add a row"
        className="rounded-control text-body w-full min-w-0 border px-2.5 py-1.5 outline-none sm:max-w-md"
        style={{ borderColor: 'var(--rule-soft)', background: 'transparent' }}
      />
    </div>
  )
}

// ── artifacts & team ─────────────────────────────────────────────────────────

/** The shelf, newest year first. Closed work stays visible: delivered things are
 *  the relationship's history, and the case for carrying on. */
function Artifacts({ artifacts }: { artifacts: Artifact[] }) {
  const years = useMemo(() => {
    const by = new Map<string, Artifact[]>()
    for (const a of artifacts) {
      const y = a.end ? a.end.slice(0, 4) : 'Undated'
      const list = by.get(y)
      if (list) list.push(a)
      else by.set(y, [a])
    }
    return [...by.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [artifacts])

  if (artifacts.length === 0) {
    return (
      <Block title="Artifacts">
        <p className="meta py-2">Nothing delivered yet.</p>
      </Block>
    )
  }

  return (
    <Block title="Artifacts">
      {years.map(([year, list]) => (
        <div key={year} className="mt-5 first:mt-0 sm:grid sm:grid-cols-[4.5rem_minmax(0,1fr)]">
          <div className="num text-body pt-2 font-semibold" style={{ color: 'var(--brass)' }}>
            {year}
          </div>
          <div>
            {list.map((a) => (
              <ArtifactRow key={a.id} artifact={a} />
            ))}
          </div>
        </div>
      ))}
    </Block>
  )
}

function ArtifactRow({ artifact }: { artifact: Artifact }) {
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b py-2.5 last:border-b-0"
      style={{ borderColor: 'var(--rule-soft)' }}
    >
      <span className="meta shrink-0" style={{ minWidth: '6rem' }}>
        {artifact.kind || '·'}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-body">{artifact.name}</span>
        {/* The one place this page is not only a document: a delivered thing you
            cannot open is just a row in a table. */}
        {artifact.links.length > 0 && (
          <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {artifact.links.map((l) => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noreferrer noopener"
                className="meta underline underline-offset-2"
                style={{ color: 'var(--marine)' }}
              >
                {l.label || hostPath(l.url)} ↗
              </a>
            ))}
          </span>
        )}
      </span>
      <span className="num text-meta">{artifact.end ? formatDate(artifact.end) : '·'}</span>
    </div>
  )
}

function Team({ people }: { people: View['people'] }) {
  return (
    <Block title="Team">
      {people.length === 0 ? (
        <p className="meta py-2">Nobody listed.</p>
      ) : (
        // The same row the internal People list draws: avatar, name, role. It was
        // a wrap of pill-shaped chips, which reads as tags rather than as people.
        <div>
          {people.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2.5 border-b py-2.5 last:border-b-0"
              style={{ borderColor: 'var(--rule-soft)' }}
            >
              <Avatar actor={{ id: p.id, name: p.name || '?' }} size={20} />
              <span className="text-body min-w-0 flex-1 truncate font-medium">
                {p.name || 'Unnamed'}
              </span>
              <span className="text-body" style={{ color: 'var(--muted)' }}>
                {p.role || '·'}
              </span>
            </div>
          ))}
        </div>
      )}
    </Block>
  )
}

/**
 * The first screen, when there is no session.
 *
 * It asks for an email and says the same thing whatever the answer: the roster is
 * the team's People list, and this box must not read it back to whoever is
 * holding the URL. So "check your inbox" is the response to an address that
 * matched and to one that did not.
 */
function SignInBox({ token, expired }: { token: string; expired: boolean }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [brand, setBrand] = useState<Brand>(DEFAULT_BRAND)
  const [org, setOrg] = useState('')

  // The page's own name and colour, before there is a session. Same route the
  // team's door uses, and the reason both look like the team rather than like us.
  useEffect(() => {
    fetch(`/api/w/${encodeURIComponent(workspaceSlug())}/brand`)
      .then((r) => r.json())
      .then((d: { brand?: Brand; workspace?: { name?: string } }) => {
        if (d.brand) setBrand(normalizeBrand(d.brand))
        if (d.workspace?.name) setOrg(d.workspace.name)
      })
      .catch(() => undefined)
  }, [])

  const ask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || busy) return
    setBusy(true)
    setError('')
    try {
      await fetch(
        `/api/w/${encodeURIComponent(workspaceSlug())}/client/${encodeURIComponent(token)}/signin`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email }),
        },
      )
      setSent(true)
    } finally {
      setBusy(false)
    }
  }

  const redeem = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const res = await fetch('/api/auth/sign-in/email-otp', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: code.trim() }),
      })
      if (!res.ok) throw new Error('bad code')
      location.reload()
    } catch {
      setError('That code did not work. Check the newest email, or ask for another.')
      setBusy(false)
    }
  }

  return (
    <Doorway
      brand={brand}
      wordmark={org || 'Your page'}
      // A label, not a claim. It said "What we are building for you, as it stands
      // today", which is a sentence about the page rather than a name for it — and
      // a client arriving at a login does not need to be told what a project page
      // is for in the display serif.
      headline={<>Project overview, tracking, and artifacts</>}
      lede={
        <>
          The projects the team has under way, where each item stands, what is waiting on you, and
          the files that have been delivered. It updates as they work.
        </>
      }
    >
      {expired ? (
        <p
          className="rounded-control note mb-4 border px-3 py-2 leading-relaxed"
          style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
        >
          That link has expired or has already been used. Ask for another.
        </p>
      ) : null}

      {sent ? (
        <form onSubmit={redeem} className="flex flex-col">
          <DoorHeading note={<>Open it and tap the button, or type the code below.</>}>
            Check your email
          </DoorHeading>
          <p className="text-body mt-2.5 mb-6 leading-relaxed">
            If <b>{email.trim()}</b> is on this project, it is on its way.
          </p>
          <DoorLabel htmlFor="client-code">Code from the email</DoorLabel>
          <input
            id="client-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
            className="num rounded-control text-title min-h-11 border px-3 py-2.5 text-center outline-none"
            style={{ ...doorField, letterSpacing: '0.3em' }}
          />
          <button
            type="submit"
            disabled={busy || code.length < 6}
            className="rounded-control text-body mt-3 min-h-11 px-3 font-semibold disabled:opacity-50"
            style={doorButton}
          >
            {busy ? 'Signing in…' : 'Open my page'}
          </button>
          {error ? (
            <p className="text-meta mt-3" style={{ color: 'var(--crit)' }}>
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setSent(false)
              setCode('')
              setError('')
            }}
            className="meta mt-4 self-start underline underline-offset-2"
          >
            use a different address
          </button>
        </form>
      ) : (
        <form onSubmit={ask} className="flex flex-col">
          <DoorHeading note={<>Use the email the team knows you by.</>}>Sign in</DoorHeading>
          <div className="mt-6" />
          <DoorLabel htmlFor="client-email">Email address</DoorLabel>
          <input
            id="client-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourcompany.com"
            autoFocus
            className="rounded-control text-body min-h-11 w-full border px-3 py-2.5 outline-none"
            style={doorField}
          />
          <button
            type="submit"
            disabled={busy || !email.includes('@')}
            className="rounded-control text-body mt-2.5 min-h-11 w-full px-3 font-semibold disabled:opacity-50"
            style={doorButton}
          >
            {busy ? 'Sending…' : 'Email me a sign-in link'}
          </button>
          {/* The whole of it, in one place. The heading used to carry a second
              sentence explaining that the email holds a link and a code, which is
              a mechanism nobody reads a login screen to learn — and the screen that
              needs it ("Check your email") says it there. */}
          <DoorFinePrint>
            Links go only to people the team has on this project. If nothing arrives, ask someone on
            the team to check the address they have for you, or to add the one you want.
          </DoorFinePrint>
        </form>
      )}
    </Doorway>
  )
}

function CountBar({ counts }: { counts: Counts }) {
  const total = counts.done + counts.open
  if (total === 0) {
    return (
      <span
        className="block h-2 rounded-full"
        style={{ background: 'var(--rule-soft)' }}
        aria-hidden
      />
    )
  }
  const pct = (n: number) => `${((n / total) * 100).toFixed(1)}%`
  return (
    <span
      className="flex h-2 overflow-hidden rounded-full"
      style={{ background: 'var(--rule-soft)' }}
      aria-hidden
    >
      <span style={{ width: pct(counts.done), background: 'var(--marine)' }} />
      <span style={{ width: pct(counts.inFlight), background: 'var(--ember)' }} />
      <span style={{ width: pct(counts.blocked), background: 'var(--crit)' }} />
    </span>
  )
}

function Legend({ counts }: { counts: Counts }) {
  const todo = Math.max(counts.open - counts.inFlight - counts.blocked, 0)
  return (
    <>
      {counts.done} done · {counts.inFlight} in flight ·{' '}
      <span style={counts.blocked > 0 ? { color: 'var(--crit)' } : undefined}>
        {counts.blocked} blocked
      </span>{' '}
      · {todo} to come
    </>
  )
}

/** Running · held · finished. Derived in the Worker, never stored — `held` is the
 *  one worth having, and no field records it. */
function StateChip({ state }: { state: Project['state'] }) {
  const tone =
    state === 'held' ? 'var(--crit)' : state === 'finished' ? 'var(--muted)' : 'var(--marine)'
  return (
    <span className="pill" style={{ ['--pc' as string]: tone }}>
      {state}
    </span>
  )
}

function Shell({
  brand,
  updatedAt,
  me,
  token,
  children,
}: {
  brand: Brand | null
  updatedAt?: string
  me?: { name: string; email: string; staff: boolean } | null
  token?: string
  children: React.ReactNode
}) {
  const name = brand ? displayName(brand) : ''
  const logo = brand?.logoKey ? (
    <img src={blobUrl(brand.logoKey)} alt="" className="h-7 w-7 rounded-[30%]" />
  ) : (
    <Mark size={24} />
  )
  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--bg)', color: 'var(--fg)' }}
    >
      {/* The app's own frame (Page.tsx), not a narrower one of this page's own. The
          document has no rail, so the board on it gets the full 1180px — which is
          more room than the same board has on the team's project page, and the
          reason a client clicking into a project can read every column. */}
      <div className="mx-auto w-full max-w-[1180px] flex-1 px-5 py-10 sm:px-8">
        <div className="mb-8 flex items-center gap-2.5">
          {logo}
          <span className="text-lead font-semibold tracking-tight">{name}</span>
          {/* The one claim this page makes about itself, and the only place it is
              made now that the counts above it are gone. */}
          <span className="ml-auto flex items-baseline gap-4">
            {updatedAt && (
              <span className="meta" title={updatedAt}>
                Updated {relativeTime(updatedAt)}
              </span>
            )}
            {/* A member looking at someone else's page is told so rather than
                addressed as though it were theirs — and gets no sign-out, because
                signing out of their own team from here would be a trap. */}
            {me?.staff && <span className="meta">Staff preview &#183; read only</span>}
            {me && !me.staff && token && (
              <span className="meta flex items-baseline gap-2">
                <span title={me.email}>{me.name || me.email}</span>
                <button
                  type="button"
                  onClick={() => {
                    void fetch(`/api/auth/sign-out`, {
                      method: 'POST',
                    }).then(() => location.reload())
                  }}
                  className="underline"
                >
                  sign out
                </button>
              </span>
            )}
          </span>
        </div>
        {children}
      </div>

      {/* Quiet, and the team's mark rather than ours: whose page this is should
          read from the bottom of it as well as the top.

          There WAS a "Made with crum" link out here. It came off: the page is served
          from crum.saltbark.com, so a client who wants to know what this is has the
          answer in the address bar, and a footer that advertises us on a page the
          team sends to their own client is one we are borrowing rather than
          giving. */}
      <footer className="mt-10 border-t" style={{ borderColor: 'var(--rule-soft)' }}>
        <div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-5 sm:px-8">
          <span className="flex items-center gap-2" style={{ opacity: 0.8 }}>
            {logo}
            {name && <span className="meta">{name}</span>}
          </span>
        </div>
      </footer>
    </div>
  )
}

function Block({
  title,
  note,
  right,
  children,
}: {
  title: string
  note?: string
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="mb-9">
      <div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
        {note && <span className="meta">{note}</span>}
        {right && <span className="ml-auto">{right}</span>}
      </div>
      {children}
    </section>
  )
}
